import { CheckCircle, MapPin, Storefront, WarningCircle } from '@liveauctioneers/hammer-ui-core/icons';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, BodySecondary, LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import useVisibilitySensor from '@rooks/use-visibility-sensor';
import { useCallback, useEffect, useRef, useState } from 'react';

import CardImage from '@/components/GridItemCard/Card/CardOptions/CategorySearchCards/components/CardImage/CardImage';
import { CardInfo } from '@/components/GridItemCard/Card/CardOptions/TimedPlusItemCard/components/CardInfo/CardInfo';
import TimedBadgeContainer from '@/components/GridItemCard/Card/CardOptions/TimedPlusItemCard/components/TimedBadgeContainer/TimedBadgeContainer';
import ItemCardPriceRowSearchCard from '@/components/GridItemCard/Card/components/ItemCardPriceRow/ItemCardPriceRowSearchCard';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useItemOrPriceResultLink } from '@/hooks/useItemOrPriceResultLink';
import { usePrevious } from '@/hooks/usePrevious';
import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryBidCount,
    getItemSummaryBidderHasBid,
    getItemSummaryBidderHasHighBid,
    getItemSummaryIndex,
    getItemSummaryIsAvailable,
    getItemSummaryIsPlacingBid,
    getItemSummaryIsSold,
    getItemSummaryLotLocationDisplay,
    getItemSummaryPromoted,
    getItemSummarySellerLocation,
    getItemSummarySellerName,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardNavigationTrigger } from '@/utils/amplitude/analytics';
import { LotRecommendationTrigger } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import noop from '@/utils/noop';

const MOBILE_IMAGE_QUALITY = 70;
const DESKTOP_IMAGE_QUALITY = 80;

const MOBILE_DIMENSION_WIDTH = 160;
const MOBILE_MIN_HEIGHT = 336;
const DESKTOP_DIMENSION_WIDTH = 280;
const DESKTOP_DIMENSION_HEIGHT = 472;

const TimedShelfCard: ItemCardComponent = ({
    className,
    itemId,
    pagePosition,
    recommendationPosition,
    recommendationType,
    trackClick = noop,
    trackVisible = noop,
}) => {
    const formatMessage = useAppFormatMessage();
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const bidderHasHighBid = useAppSelector((state) => getItemSummaryBidderHasHighBid(state, itemId));
    const isAvailable = useAppSelector((state) => getItemSummaryIsAvailable(state, itemId));
    const isPlacingBid = useAppSelector((state) => getItemSummaryIsPlacingBid(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));
    const {
        city: lotLocationCity,
        countryCode: lotLocationCountryCode,
        region: lotLocationRegion,
    } = useAppSelector((state) => getItemSummaryLotLocationDisplay(state, itemId));
    const sellerHouseLocation = useAppSelector((state) => getItemSummarySellerLocation(state, itemId));
    const lotLocation =
        lotLocationCity === lotLocationRegion
            ? [lotLocationCity, lotLocationCountryCode].filter(Boolean).join(', ')
            : [lotLocationCity, lotLocationRegion].filter(Boolean).join(', ');
    const sellerLocation = lotLocation || sellerHouseLocation;
    const isLeading = isAvailable && bidderHasHighBid;
    const isOutbid = isAvailable && bidderHasBid && !bidderHasHighBid && !isPlacingBid;
    const itemUrl = useItemOrPriceResultLink(itemId);
    const { onNavigateInitiated } = useItemCardNavigation(itemId);

    const trackingPosition = pagePosition || index;

    const [linkHover, setLinkHover] = useState(false);
    const isMobile = useIsMobileLayout();
    const cardRef = useRef(null);
    const [hasVisibilityBeenTracked, setHasVisibilityBeenTracked] = useState(false);
    const { isVisible } = useVisibilitySensor(cardRef, {
        intervalCheck: false,
        resizeCheck: true,
        scrollCheck: true,
    });

    const previousIsVisible = usePrevious(isVisible);

    useEffect(() => {
        const isNewlyVisible = !previousIsVisible && isVisible;
        if (isNewlyVisible && !hasVisibilityBeenTracked) {
            trackVisible?.(itemId, promoted);
            setHasVisibilityBeenTracked(true);
        }
    }, [isVisible, itemId, previousIsVisible, promoted, trackVisible, hasVisibilityBeenTracked]);

    const enterLinkHoverState = useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = useCallback(() => {
        setLinkHover(false);
    }, []);

    const onCardClick = useCallback(() => {
        if (!isSold) {
            onNavigateInitiated(ItemCardNavigationTrigger.ImageClick, { position: trackingPosition });
        }
        trackClick(itemId, trackingPosition);
    }, [isSold, trackClick, itemId, trackingPosition, onNavigateInitiated]);

    const onRecommendationClick = useRecommendationTracking({
        itemId,
        recommendationPosition: recommendationPosition ?? 0,
        recommendationType,
    });

    const imageContainerStyle = isMobile
        ? { height: `${MOBILE_DIMENSION_WIDTH}px`, width: `${MOBILE_DIMENSION_WIDTH}px` }
        : {};

    return (
        <article
            className={cn(
                'mb-3xs flex w-auto flex-col overflow-hidden rounded-md border border-border-secondary',
                className
            )}
            data-testid={`timed-shelf-card-${itemId}`}
            onClick={onCardClick}
            ref={cardRef}
            style={
                isMobile
                    ? { minHeight: `${MOBILE_MIN_HEIGHT}px`, width: `${MOBILE_DIMENSION_WIDTH}px` }
                    : { height: `${DESKTOP_DIMENSION_HEIGHT}px`, width: `${DESKTOP_DIMENSION_WIDTH}px` }
            }
        >
            <section className="flex flex-1 flex-col">
                <TimedBadgeContainer itemId={itemId} />
                <div
                    className="overflow-hidden border-b border-border-secondary"
                    style={imageContainerStyle}
                >
                    <CardImage
                        applyDimensionToUrl
                        canShowEasyShipping
                        dimension={isMobile ? MOBILE_DIMENSION_WIDTH : DESKTOP_DIMENSION_WIDTH}
                        enterLinkHoverState={enterLinkHoverState}
                        imageQuality={isMobile ? MOBILE_IMAGE_QUALITY : DESKTOP_IMAGE_QUALITY}
                        isLabeledFavoriteButton={false}
                        isMobile={isMobile}
                        itemId={itemId}
                        lazyLoadImage
                        leaveLinkHoverState={leaveLinkHoverState}
                        onNavigate={() => {
                            onNavigateInitiated(ItemCardNavigationTrigger.ImageClick, {
                                position: trackingPosition,
                            });
                            onRecommendationClick(LotRecommendationTrigger.ImageClick);
                        }}
                        sharpenImage
                        shouldShowSeeSimilarItemsButtonIconCTA
                    />
                </div>
                <div className="flex flex-1 flex-col gap-2xs p-xs smMin:p-sm">
                    <div>
                        <CardInfo
                            className="!text-xs"
                            hasNewSimilarItemsIconCTA
                            isMobile={isMobile}
                            isOpenItem={false}
                            itemId={itemId}
                            onNavigate={(trigger) => {
                                onNavigateInitiated(trigger, { position: trackingPosition });
                                onRecommendationClick(LotRecommendationTrigger.HouseNameClick);
                            }}
                        />
                    </div>
                    <div>
                        <SubtleMonochromeLink
                            aria-label={title}
                            className="line-clamp-2"
                            href={itemUrl}
                            onClick={() => {
                                onNavigateInitiated(ItemCardNavigationTrigger.TitleClick, {
                                    position: trackingPosition,
                                });
                                onRecommendationClick(LotRecommendationTrigger.TitleClick);
                            }}
                            size="sm"
                            title={title}
                        >
                            {isMobile ? (
                                <BodySecondary>{title}</BodySecondary>
                            ) : (
                                <BodyPrimary className="text-sm">{title}</BodyPrimary>
                            )}
                        </SubtleMonochromeLink>
                    </div>
                    <div className="relative flex items-center justify-between">
                        <ItemCardPriceRowSearchCard
                            isHovered={linkHover}
                            itemId={itemId}
                            onMouseEnter={enterLinkHoverState}
                            onMouseLeave={leaveLinkHoverState}
                            onNavigate={() => {
                                onNavigateInitiated(ItemCardNavigationTrigger.TitleClick, {
                                    position: trackingPosition,
                                });
                                onRecommendationClick(LotRecommendationTrigger.TitleClick);
                            }}
                            showBidCount={Boolean(bidCount)}
                            shouldUseCarouselFontSize
                        />
                    </div>
                    {(isLeading || isOutbid) && (
                        <div>
                            <LabelMini
                                className="inline-flex items-center gap-x-2xs"
                                color={isLeading ? 'success' : 'warning'}
                            >
                                {isLeading ? (
                                    <CheckCircle
                                        color="success"
                                        size="xs"
                                        style="fill"
                                    />
                                ) : (
                                    <WarningCircle
                                        color="warning"
                                        size="xs"
                                        style="fill"
                                    />
                                )}
                                {isLeading ? formatMessage(['leading']) : formatMessage(['outbid'])}
                            </LabelMini>
                        </div>
                    )}
                    <div className="mt-auto flex flex-col gap-y-2xs">
                        <BodySecondary className="flex items-baseline gap-x-2xs text-xs text-text-secondary">
                            <span className="inline-flex shrink-0 translate-y-[2px] leading-none">
                                <MapPin size="xs" />
                            </span>
                            <span className="truncate">{sellerLocation}</span>
                        </BodySecondary>

                        <BodySecondary className="flex items-baseline gap-x-2xs text-xs text-text-secondary">
                            <span className="inline-flex shrink-0 translate-y-[2px] leading-none">
                                <Storefront size="xs" />
                            </span>
                            <span className="truncate">{sellerName}</span>
                        </BodySecondary>
                    </div>
                </div>
            </section>
        </article>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: TimedShelfCard,
};

export default info;
